import React, { useMemo, useCallback, useState, useEffect } from "react";
import { GameBoard } from "./components/game-board.tsx";
import { GameControls } from "./components/game-controls.tsx";
import { useGame } from "./hooks/use-game.ts";
import { getDailyPuzzle, fetchDailyPuzzle, getPuzzleNumber } from "./lib/daily.ts";
import { loadStats, saveStats, recordCompletion } from "./lib/storage.ts";
import type { GameState, Puzzle } from "./types.ts";

export function App({ onWin, embedded, winExtra, difficulty }: { onWin?: (data: { stars: number; metric: number; timeMs: number }) => void; embedded?: boolean; winExtra?: React.ReactNode; difficulty?: string } = {}) {
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const puzzleNumber = useMemo(() => getPuzzleNumber(), []);

  useEffect(() => {
    setPuzzle(null);
    fetchDailyPuzzle(difficulty)
      .then(setPuzzle)
      .catch(() => setPuzzle(getDailyPuzzle(difficulty)));
  }, [difficulty]);

  if (!puzzle) {
    return (
      <div className={`flex flex-col items-center justify-center ${embedded ? 'p-4 min-h-[300px]' : 'min-h-screen p-6'} max-w-[700px] mx-auto select-none`}>
        <div className="animate-pulse text-slate-500 text-sm font-mono">Loading puzzle...</div>
      </div>
    );
  }

  return <GameInner puzzle={puzzle} puzzleNumber={puzzleNumber} onWin={onWin} embedded={embedded} winExtra={winExtra} />;
}

function GameInner({ puzzle, puzzleNumber, onWin, embedded, winExtra }: { puzzle: Puzzle; puzzleNumber: number; onWin?: (data: { stars: number; metric: number; timeMs: number }) => void; embedded?: boolean; winExtra?: React.ReactNode }) {
  const {
    state, clueStatuses,
    selectInventory, place, rotate, pickup,
    submit, undo, reset,
  } = useGame(puzzle);
  const [showHelp, setShowHelp] = useState(false);
  const [stats, setStats] = useState(() => loadStats());

  const won = state.phase === "won";

  useEffect(() => {
    if (won && !stats.history[puzzle.id]?.completed) {
      const timeMs = (state.completedAt ?? Date.now()) - state.startedAt;
      const updated = recordCompletion(stats, {
        puzzleId: puzzle.id,
        completed: true,
        stars: state.stars,
        moves: state.moves,
        timeMs,
      });
      setStats(updated);
      saveStats(updated);
      onWin?.({ stars: state.stars, metric: state.moves, timeMs });
    }
  }, [won]);

  return (
    <div className={`flex flex-col items-center ${embedded ? 'p-4 gap-4' : 'min-h-screen p-6 gap-6'} max-w-[700px] mx-auto select-none`}>
      {/* Header — hidden when embedded */}
      {!embedded && (
        <div className="flex items-center justify-between w-full">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              <span className="text-cyan-400">Echo</span>
            </h1>
            <div className="flex gap-3 text-sm text-slate-500">
              <span>#{puzzleNumber}</span>
              <span className="text-slate-600">
                {puzzle.difficulty.name} ({puzzle.difficulty.rows}x{puzzle.difficulty.cols})
              </span>
              {stats.streak > 0 && (
                <span className="text-cyan-400">{stats.streak} day streak</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {state.inventory.length > 0 && state.phase === "placing" && !state.heldEmitterId && (
              <span className="text-xs text-cyan-400 animate-pulse">
                Click an emitter below
              </span>
            )}
            {state.heldEmitterId && (
              <span className="text-xs text-cyan-300 animate-pulse">
                Click a cell to place
              </span>
            )}
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-800 text-slate-400 hover:text-white text-sm font-bold border border-slate-700"
            >
              ?
            </button>
          </div>
        </div>
      )}

      {/* Subheader when embedded */}
      {embedded && (
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs text-slate-500">#{puzzleNumber}</span>
            <span className="font-mono text-xs text-slate-600">{puzzle.difficulty.name}</span>
            {state.inventory.length > 0 && state.phase === "placing" && !state.heldEmitterId && (
              <span className="text-xs text-[#00F0A0] animate-pulse">Click an emitter below</span>
            )}
            {state.heldEmitterId && (
              <span className="text-xs text-[#00F0A0] animate-pulse">Click a cell to place</span>
            )}
            {stats.streak > 0 && (
              <span className="font-mono text-xs text-[#00F0A0]">{stats.streak} day streak</span>
            )}
          </div>
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-[#1A1D33] text-slate-400 hover:text-white text-xs font-bold border border-[#2A2D42]"
          >
            ?
          </button>
        </div>
      )}

      {/* Help */}
      {showHelp && (
        <div className="w-full bg-slate-800/80 rounded-xl p-4 text-sm text-slate-300 border border-slate-700 space-y-2">
          <p className="font-medium text-white">How to play</p>
          <p>
            Place and orient emitters so every clue number is satisfied.
          </p>
          <ol className="list-decimal list-inside space-y-1">
            <li>
              <strong>Click an emitter</strong> from the inventory at the bottom
            </li>
            <li>
              <strong>Click an empty cell</strong> on the board to place it
            </li>
            <li>
              <strong>Click a placed emitter</strong> to rotate it (changes beam directions)
            </li>
            <li>
              <strong>Right-click</strong> a placed emitter to pick it up
            </li>
            <li>
              Each emitter sends <strong className="text-cyan-400">beams</strong> from its arms
              for a given range. The number on the emitter is its range.
            </li>
            <li>
              <strong>Clue numbers</strong> show how many beams must pass through that cell
            </li>
            <li>
              <span className="text-green-400">Green</span> = satisfied,{" "}
              <span className="text-red-400">Red</span> = too many,{" "}
              <span className="text-slate-400">Gray</span> = not enough
            </li>
          </ol>
          <p className="text-slate-500">
            Beams stop at walls, grid edges, and other emitters. Esc to deselect.
          </p>
        </div>
      )}

      {/* Game board */}
      <GameBoard
        board={state.board}
        placedEmitters={state.placedEmitters}
        inventory={state.inventory}
        heldEmitterId={state.heldEmitterId}
        clueStatuses={clueStatuses}
        phase={state.phase}
        onSelectInventory={selectInventory}
        onPlace={place}
        onRotate={rotate}
        onPickup={pickup}
      />

      {/* Controls */}
      <GameControls
        moves={state.moves}
        canUndo={state.moves > 0 && !won}
        phase={state.phase}
        blind={state.puzzle.difficulty.blind}
        submitted={state.submitted}
        startedAt={state.startedAt}
        onUndo={undo}
        onReset={reset}
        onSubmit={submit}
      />

      {/* Win */}
      {won && (
        <div className="w-full bg-cyan-900/40 border border-cyan-700 rounded-xl p-6 text-center space-y-3 animate-[fadeIn_0.3s_ease-out]">
          <div className="text-4xl">
            {"\u2605".repeat(state.stars)}
            {"\u2606".repeat(3 - state.stars)}
          </div>
          <p className="text-xl font-bold text-cyan-300">All Signals Aligned!</p>
          <p className="text-sm text-slate-400">
            {state.moves} move{state.moves !== 1 ? "s" : ""}
            {" \u2014 "}
            {state.stars === 3
              ? "Perfect solve!"
              : state.stars === 2
                ? "Great job!"
                : "You got it!"}
          </p>
          <ShareButton state={state} puzzleNumber={puzzleNumber} />
          {stats.totalPlayed > 0 && !embedded && (
            <div className="pt-3 border-t border-cyan-800 text-xs text-slate-500 flex justify-center gap-6">
              <span>
                Played:{" "}
                <strong className="text-slate-300">{stats.totalPlayed}</strong>
              </span>
              <span>
                Stars:{" "}
                <strong className="text-amber-400">{stats.totalStars}</strong>
              </span>
              <span>
                Best streak:{" "}
                <strong className="text-slate-300">{stats.maxStreak}</strong>
              </span>
            </div>
          )}
          {winExtra}
        </div>
      )}
    </div>
  );
}

function ShareButton({
  state,
  puzzleNumber,
}: {
  state: GameState;
  puzzleNumber: number;
}) {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(() => {
    const timeMs = (state.completedAt ?? Date.now()) - state.startedAt;
    const minutes = Math.floor(timeMs / 60000);
    const seconds = Math.floor((timeMs % 60000) / 1000);
    const timeStr = `${minutes}:${String(seconds).padStart(2, "0")}`;

    const text = [
      `\uD83D\uDCE1 Echo #${puzzleNumber}`,
      `${"\u2605".repeat(state.stars)}${"\u2606".repeat(3 - state.stars)} (solved in ${timeStr})`,
    ].join("\n");

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [state, puzzleNumber]);

  return (
    <button
      onClick={handleShare}
      className="px-6 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg text-sm font-medium transition-colors"
    >
      {copied ? "Copied!" : "Share Result"}
    </button>
  );
}
